import { Component, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { LoggerService } from '@service/logger.service';
import { SettingsService } from '@service/settings.service';

type ColorChoices = 'primary' | 'basic';
type Func = () => void;

interface StyleSelection {
  color: ColorChoices;
}

@Component({
  selector: 'reg-exp-dialog',
  styleUrls: ['./reg-exp-dialog.component.scss'],
  templateUrl: './reg-exp-dialog.component.html',
  imports: [
    MatCard,
    MatDialogTitle,
    FormsModule,
    ReactiveFormsModule,
    MatLabel,
    MatFormField,
    MatInput,
    MatHint,
    MatError,
    MatDialogActions,
    MatButton,
    MatIcon,
    MatTooltip,
  ],
})
export class RegExpDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<RegExpDialogComponent>>(MatDialogRef);
  private settingsService = inject(SettingsService);
  private formBuilder = inject(FormBuilder);
  data = inject<{
    title: string;
    regexpHint: string;
    caseNameHint: string;
    regExpStr: string;
    button1: {
      text: string;
      action?: Func;
      style: StyleSelection;
    };
    button2: {
      text: string;
      action?: Func;
      style: StyleSelection;
    };
    button3: {
      text: string;
      action?: Func;
      style: StyleSelection;
    };
  }>(MAT_DIALOG_DATA);

  private DEFAULT_REGEXP = '(?:^|\\s*\\(*)([^\\s,\\(]*-[^\\s,\\)]*)(?:$|\\s*,*\\)*)';
  regExpForm!: FormGroup;
  regExpTestInputSuccess = '';
  regExpTestInputFailure = '';
  loggerService = inject(LoggerService);

  constructor() {
    this.data.regExpStr = this.loadSavedRegExpOrDefault();
  }

  ngOnInit(): void {
    this.regExpForm = this.formBuilder.group({
      regExpInput: [this.data.regExpStr, [Validators.required, validRegExpValidator()]],
      regExpTestInput: ['', null],
    });
  }

  /**
   * Handles the click event for Button 1.
   *
   * This method resets the success and failure messages, validates the form,
   * and tests the regular expression input against the test input. If the
   * regular expression matches the test input, it sets the success message
   * with the match result. Otherwise, it sets the failure message.
   */
  onButton1Click(): void {
    this.regExpTestInputSuccess = '';
    this.regExpTestInputFailure = '';
    if (this.regExpForm.valid) {
      const regExpValue = this.regExpForm.get('regExpInput')?.value;
      const regExpTestInput = this.regExpForm.get('regExpTestInput')?.value;
      if (regExpValue && regExpTestInput) {
        const testingRegExp = new RegExp(regExpValue);
        const match = testingRegExp.exec(regExpTestInput);
        if (match && match[1].length > 0) {
          this.regExpTestInputSuccess = `Case Match: ${match[1]}`;
        } else {
          this.regExpTestInputFailure = 'No match found';
        }
      } else {
        this.regExpTestInputFailure = 'Missing Requirements';
      }
    } else {
      this.regExpTestInputFailure = 'Invalid Regular Expression';
    }
  }

  /**
   * Resets the regular expression input to the default value.
   */
  onButton2Click(): void {
    this.regExpForm.get('regExpInput')?.setValue(this.DEFAULT_REGEXP);
    this.regExpTestInputSuccess = '';
    this.regExpTestInputFailure = '';
  }

  /**
   * Saves the regular expression input if valid and closes the dialog.
   */
  onButton3Click(): void {
    if (this.regExpForm.valid) {
      const regExpValue = this.regExpForm.get('regExpInput')?.value;
      this.saveRegExp(regExpValue);
      this.dialogRef.close('button2');
    } else {
      this.regExpTestInputFailure = 'Invalid Regular Expression';
    }
  }

  onClose() {
    this.loggerService.info('[RegExpDialogComponent] Close clicked, closing dialog');
    this.dialogRef.close();
  }

  /**
   *  Saves the provided regular expression value to the settings service.
   * @param regExpValue
   */
  saveRegExp(regExpValue: string) {
    this.settingsService.setRegExp(regExpValue);
  }

  /**
   * Loads the previous regExp for the current user
   */
  loadSavedRegExpOrDefault(): string {
    const settings = this.settingsService.getSettings();
    return settings.regExp;
  }
}

/**
 * Validator function to check if a control's value is a valid regular expression.
 *
 * @returns {function(AbstractControl): (ValidationErrors | null)} A function that takes an AbstractControl and returns ValidationErrors or null.
 */
export function validRegExpValidator(): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    try {
      const { value } = control;
      if (!value.includes('(') || !value.includes(')')) {
        return { invalidRegExp: 'Missing Group' };
      }
      if (/\{}/.test(value)) {
        return { invalidRegExp: 'Empty Quantifier' };
      }
      const testingRegExp = new RegExp(value);
      testingRegExp.test('test'); // this is looking for malformed regex only
      return null;
    } catch (e) {
      if (e instanceof Error) {
        return { invalidRegExp: `${e.message.substring(e.message.lastIndexOf(':') + 1)}` };
      } else {
        return { invalidRegExp: 'Invalid Regular Expression' };
      }
    }
  };
}
